<template>
    <div class="configure-from-price-tag">
        <div
            v-if="selectedTag && modelValue.templateStructure"
            class="configure-from-price-tag__workspace"
        >
            <div class="configure-from-price-tag__editor">
                <v-alert
                    v-if="!hasEditableFields"
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="configure-from-price-tag__notice"
                >
                    {{
                        toSentenceCase(
                            $t(
                                isTagLocked
                                    ? 'admin.offerPresets.wizard.noEditableTagValuesSystem'
                                    : 'admin.offerPresets.wizard.noEditableTagValues'
                            )
                        )
                    }}
                </v-alert>
                <price-tag-editor
                    :price-tag="editorTag"
                    :offer-mechanic="modelValue.templateStructure"
                    :display-values="modelValue.displayValues"
                    :model-value="values"
                    :context-source="contextSource"
                    @update:model-value="updateValues"
                />
            </div>
            <offer-preset-restrictions
                class="configure-from-price-tag__restrictions"
                :offer-mechanic="modelValue.templateStructure"
                @update="updateRestrictions"
            />
        </div>
        <p v-else class="configure-from-price-tag__empty">
            {{ $t('admin.offerPresets.wizard.pickPriceTag') }}
        </p>
        <div class="configure-from-price-tag__gallery">
            <span class="configure-from-price-tag__label">
                {{
                    toSentenceCase(
                        $t(
                            isTagLocked
                                ? 'admin.offerPresets.wizard.attachedPriceTag'
                                : 'admin.offerPresets.wizard.choosePriceTag'
                        )
                    )
                }}
            </span>
            <price-tag-gallery
                carousel
                :items="galleryItems"
                :selected-id="modelValue.priceTagId"
                :context-source="contextSource"
                @select="selectTag"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import {
    readValuesFromMechanic,
    applyValuesToMechanic,
} from '@/js/utils/price-tags/binding-engine';
import { validatePriceTagValues } from '@/js/utils/price-tags/validate-values';
import offerMechanicTemplates from '@enums/offer-mechanic-templates';
import priceTagFieldSections from '@enums/price-tag-field-sections';
import { toSentenceCase } from '@/js/utils/string-utils';
import { getPriceTagLabel } from '@/js/utils/offer-template-label';

const props = defineProps({
    modelValue: { type: Object, required: true },
    priceTags: { type: Array, default: () => [] },
    presets: { type: Array, default: () => [] },
    contextSource: { type: Object, required: true },
});
const emit = defineEmits(['update:modelValue']);
const values = ref({});

const selectedTag = computed(() =>
    props.priceTags.find(tag => tag.id === props.modelValue.priceTagId)
);
// System presets keep their attached tag so planners and reset-to-default stay consistent.
const isTagLocked = computed(
    () => props.modelValue.source === 'system' && !!props.modelValue.priceTagId
);
// Restriction bindings are edited in the restrictions panel, so the tag editor only owns tag fields.
const tagFields = computed(() =>
    (selectedTag.value?.fields || []).filter(
        field => field.section !== priceTagFieldSections.restrictions
    )
);
const editorTag = computed(
    () => selectedTag.value && { ...selectedTag.value, fields: tagFields.value }
);
const hasEditableFields = computed(() => tagFields.value.length > 0);
const getDefaultDisplayValues = tag =>
    (tag?.fields || []).reduce((defaults, field) => {
        const match = /^displayValues\.(\w+)$/.exec(field.binding);
        if (match) defaults[match[1]] = field.defaultValue;
        return defaults;
    }, {});

const findPresetForTag = priceTagId => {
    const tag = props.priceTags.find(item => item.id === priceTagId);
    return (
        (tag?.templateStructure && {
            templateStructure: tag.templateStructure,
            displayValues: getDefaultDisplayValues(tag),
        }) ||
        props.presets.find(preset => preset.priceTagId === priceTagId) ||
        props.presets.find(
            preset => preset.offerTemplate === offerMechanicTemplates.newPricePerItem
        )
    );
};
const errors = computed(() => validatePriceTagValues(tagFields.value, values.value));
const galleryItems = computed(() =>
    props.priceTags
        .filter(tag => !isTagLocked.value || tag.id === props.modelValue.priceTagId)
        .map(tag => {
            const isDraftTag =
                tag.id === props.modelValue.priceTagId && props.modelValue.templateStructure;
            const source = isDraftTag ? props.modelValue : findPresetForTag(tag.id);
            return {
                id: tag.id,
                label: getPriceTagLabel({
                    id: tag.id,
                    name: tag.name,
                    currencySymbol: props.contextSource.currencySymbol,
                }),
                priceTag: tag,
                offerMechanic: source?.templateStructure,
                displayValues: source?.displayValues || {},
            };
        })
        .filter(item => item.offerMechanic)
);
const writeDraft = () => {
    if (!selectedTag.value || !props.modelValue.templateStructure) return;
    const { offerMechanic, displayValues } = applyValuesToMechanic({
        template: props.modelValue.templateStructure,
        fields: tagFields.value,
        values: values.value,
        transformContext: props.contextSource.transformContext,
    });
    emit('update:modelValue', {
        ...props.modelValue,
        priceTagId: selectedTag.value.id,
        templateStructure: offerMechanic,
        displayValues,
        tagValues: cloneDeep(values.value),
        isValid: Object.keys(errors.value).length === 0,
    });
};

const initialiseFromTag = priceTagId => {
    const tag = props.priceTags.find(item => item.id === priceTagId);
    const preset = findPresetForTag(priceTagId);
    if (!tag || !preset) return;
    values.value = readValuesFromMechanic({
        offerMechanic: preset.templateStructure,
        fields: tag.fields || [],
        displayValues: preset.displayValues,
        transformContext: props.contextSource.transformContext,
    });
    const { offerMechanic, displayValues } = applyValuesToMechanic({
        template: preset.templateStructure,
        fields: tag.fields || [],
        values: values.value,
        transformContext: props.contextSource.transformContext,
    });
    const fields = (tag.fields || []).filter(
        field => field.section !== priceTagFieldSections.restrictions
    );
    values.value = readValuesFromMechanic({
        offerMechanic,
        fields,
        displayValues,
        transformContext: props.contextSource.transformContext,
    });
    emit('update:modelValue', {
        ...props.modelValue,
        priceTagId,
        templateStructure: offerMechanic,
        displayValues,
        tagValues: cloneDeep(values.value),
        isValid: Object.keys(validatePriceTagValues(fields, values.value)).length === 0,
    });
};

const selectTag = priceTagId => {
    if (isTagLocked.value && priceTagId !== props.modelValue.priceTagId) return;
    if (priceTagId === props.modelValue.priceTagId && props.modelValue.templateStructure) return;
    initialiseFromTag(priceTagId);
};

const updateValues = updatedValues => {
    values.value = updatedValues;
    writeDraft();
};

const updateRestrictions = patch => {
    emit('update:modelValue', {
        ...props.modelValue,
        templateStructure: { ...cloneDeep(props.modelValue.templateStructure), ...patch },
        tagValues: cloneDeep(values.value),
    });
};

watch(
    () => [props.modelValue.priceTagId, props.modelValue.templateStructure],
    () => {
        if (!selectedTag.value || !props.modelValue.templateStructure) return;
        values.value =
            props.modelValue.tagValues ||
            readValuesFromMechanic({
                offerMechanic: props.modelValue.templateStructure,
                fields: tagFields.value,
                displayValues: props.modelValue.displayValues,
                transformContext: props.contextSource.transformContext,
            });
    },
    { immediate: true }
);

if (props.modelValue.priceTagId && !props.modelValue.templateStructure) {
    initialiseFromTag(props.modelValue.priceTagId);
}
</script>

<style scoped lang="scss">
.configure-from-price-tag {
    display: flex;
    flex-direction: column;
    gap: 2.4rem;
    font-size: 1.4rem;

    &__workspace {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 34rem;
        gap: 2.4rem;
        align-items: start;
    }

    &__editor {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        align-items: center;
        min-width: 0;
        overflow-x: auto;
    }

    &__notice {
        align-self: stretch;
        font-size: 1.3rem;
    }

    &__empty {
        margin: 0;
        padding: 4rem;
        color: #646b73;
        text-align: center;
        border: 1px dashed #dfe3e8;
        border-radius: 0.8rem;
    }

    &__gallery {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        padding-top: 1.6rem;
        border-top: 1px solid #dfe3e8;
    }

    &__label {
        font-size: 1.3rem;
        font-weight: 600;
    }
}

@media (max-width: 960px) {
    .configure-from-price-tag__workspace {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
